import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useChallenge } from './useChallenge';
import GameLobby from '../shared/GameLobby';
import challengePacks from '@/data/challengePacks.json';
import { getAllStyles } from '@utils/stylePresets';
import { RefreshCw, BookOpen, X } from 'lucide-react';

const TIMER_OPTIONS = [
  { label: '1 min', value: 60 },
  { label: '2 min', value: 120 },
  { label: '3 min', value: 180 },
  { label: '5 min', value: 300 },
];

export default function ChallengeLobby() {
  const { state } = useApp();
  const { game, user } = state;
  const { initChallenge, rerollChallenge, startDrawing, setChallengeStyle, endGame } = useChallenge();
  const [timerDuration, setTimerDuration] = useState(120);
  const [showStylePicker, setShowStylePicker] = useState(false);

  const styles = getAllStyles();
  const activeStyle = game.challenge?.style || null;

  const handleSelectPack = (packId) => {
    initChallenge(packId);
  };

  const handleSelectStyle = (style) => {
    setChallengeStyle(style);
    setShowStylePicker(false);
  };

  const handleClearStyle = () => {
    setChallengeStyle(null);
  };

  const handleStart = () => {
    if (!game.challenge) return;
    startDrawing(timerDuration);
  };

  return (
    <GameLobby
      title="Challenge Mode"
      emoji="🏆"
      onStart={handleStart}
      onCancel={endGame}
      startLabel="Start Drawing!"
      startDisabled={!game.challenge}
    >
      {/* Category selection (host only) */}
      {user.isHost && !game.challenge && (
        <div>
          <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
            Pick a Category
          </p>
          <div className="grid grid-cols-2 gap-2">
            {challengePacks.packs.map((pack) => (
              <button
                key={pack.id}
                onClick={() => handleSelectPack(pack.id)}
                className="px-3 py-2.5 rounded-xl border border-slate-200 hover:border-amber/40 hover:bg-amber/5 transition-all text-left"
              >
                <span className="text-lg mr-1.5">{pack.emoji}</span>
                <span className="text-sm font-display text-charcoal">{pack.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Challenge preview */}
      {game.challenge && (
        <div className="bg-amber/5 border border-amber/20 rounded-xl p-4">
          <p className="text-xs text-amber font-display uppercase tracking-wide mb-1">
            {game.challenge.category}
          </p>
          <p className="text-base font-semibold text-charcoal font-display">
            {game.challenge.text}
            {activeStyle && (
              <span className="text-charcoal-muted"> — in the style of </span>
            )}
            {activeStyle && (
              <span className="font-bold" style={{ color: activeStyle.colour }}>
                {activeStyle.emoji} {activeStyle.name}
              </span>
            )}
          </p>
          {user.isHost && (
            <button
              onClick={rerollChallenge}
              className="mt-2 flex items-center gap-1.5 text-xs text-amber hover:text-amber/80 font-display transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Another one
            </button>
          )}
        </div>
      )}

      {/* Style picker toggle (host only, after challenge is selected) */}
      {user.isHost && game.challenge && (
        <div>
          <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <BookOpen className="w-3 h-3" />
            Art Style <span className="text-slate-300 font-normal normal-case">(optional)</span>
          </p>

          {activeStyle ? (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl border-2"
              style={{ borderColor: activeStyle.colour + '40', backgroundColor: activeStyle.colour + '0D' }}
            >
              <span className="text-xl">{activeStyle.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-charcoal font-display truncate">
                  {activeStyle.name}
                </p>
                <p className="text-[10px] text-slate-400 font-display">
                  Palette locked to style colours
                </p>
              </div>
              <button
                onClick={handleClearStyle}
                className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-red-500 hover:bg-white transition-colors"
                title="Clear style"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowStylePicker(!showStylePicker)}
              className="w-full px-3 py-2.5 rounded-xl border border-dashed border-slate-300 hover:border-amber/50 hover:bg-amber/5 transition-colors text-sm font-display text-slate-500 hover:text-charcoal text-center"
            >
              {showStylePicker ? 'Close' : 'Draw in the style of...'}
            </button>
          )}

          {showStylePicker && !activeStyle && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {styles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => handleSelectStyle(style)}
                  className="px-3 py-2 rounded-xl border border-slate-200 hover:border-slate-300 text-left transition-all group"
                  style={{ borderLeftColor: style.colour, borderLeftWidth: '3px' }}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg">{style.emoji}</span>
                    <span className="text-xs font-semibold text-charcoal font-display">
                      {style.name}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-display mt-0.5 line-clamp-1">
                    {style.tagline}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Timer duration (host only) */}
      {user.isHost && game.challenge && (
        <div>
          <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
            Drawing Time
          </p>
          <div className="flex gap-2">
            {TIMER_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setTimerDuration(value)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                  timerDuration === value
                    ? 'bg-amber text-white font-semibold'
                    : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Non-host sees settings read-only */}
      {!user.isHost && game.challenge && (
        <p className="text-sm text-slate-400 font-display text-center">
          Waiting for host to start the challenge...
        </p>
      )}
    </GameLobby>
  );
}
